import type { AuthoredMapId, MapId } from '@cratered/shared';
import type { GroundArt } from './ground';
import { Terrain } from './terrain';

/**
 * Authored arenas: Raft Wars scenes with hand-placed ground, rafts and boats (the land maps are
 * the war maps in warmaps.ts). Everything solid is destructible; rafts are rows of buoyant
 * panels and vessels are one hull (see boats.ts). Every arena has a way across on foot: a pier,
 * a boat to board, a door through a trunk; pathing.ts checks it and a test holds it.
 */

export type Material = 'grass' | 'wood' | 'stone' | 'metal' | 'rock' | 'regolith';

export type Shape =
  | { kind: 'rect'; x: number; y: number; w: number; h: number; m: Material }
  | { kind: 'poly'; pts: [number, number][]; m: Material }
  | { kind: 'circle'; x: number; y: number; r: number; m: Material }
  /**
   * A coarse bitmap of ground: `cols x rows` cells of `cell` px, run-length encoded as base-36 counts
   * (empty run first, then solid, alternating) in row-major order. Used by the war maps, whose
   * silhouettes are too organic for rects and polygons.
   */
  | { kind: 'mask'; x: number; y: number; cell: number; w: number; h: number; rle: string; m: Material }
  /** A crater punched out of whatever was filled before it. */
  | { kind: 'hole'; x: number; y: number; r: number };

export type BoatKindDef = 'raft' | 'yacht' | 'fishing' | 'police';

export interface BoatDef {
  id: string;
  /** Centre x and the deck's waterline y. */
  x: number;
  y: number;
  /** A raft's panel width; a vessel is one panel as wide as the whole boat. */
  panelW: number;
  /** Panels in a raft (a vessel is always one body, whatever this says). */
  panels: number;
  /** Which deck ends have a stop post that keeps sliding moles aboard. */
  stops: 'both' | 'left' | 'right' | 'none';
  /** Raft by default. Vessels are one hull with one health pool: they smoke, burn, blow up and sink. */
  kind?: BoatKindDef;
  /**
   * A raised second level over part of the deck, `bays` bays of `bayW` px centred `at` px from the
   * boat's centre, with a ramp bay at each end (or just `ramps` of them) to walk up.
   */
  upper?: { at: number; bays: number; bayW: number; ramps?: 'both' | 'left' | 'right' };
}

/** What a floatie mole sits on in the water: an inflatable ring, or a jetski. */
export type FloatStyle = 'ring' | 'jetski';

/** A painted backdrop in client/public/art, see backdrop.ts. */
export type BackdropId = 'meadow' | 'desert' | 'dusk' | 'dunes' | 'city' | 'cityday' | 'storm' | 'frost' | 'cave' | 'water' | 'sunset' | 'night' | 'facility';

export interface LevelStyle {
  sky: [string, string];
  far: string;
  near: string;
  water: string;
  deep: string;
  pit: string;
  horizon: 'islands' | 'mesas' | 'city' | 'storm' | 'moon' | 'dunes' | 'cave';
  /** Painted backdrop to draw in place of the drawn sky and ridge (the drawn ones are the fallback). */
  art?: BackdropId;
  /**
   * Underground levels and building yards: the base ground carries on out past the left and right
   * edges of the map, so the world never shows a side edge, however wide the window is.
   */
  extend?: boolean;
}

/**
 * The ground that runs into one side edge of the map (the base slab of a cave, the left or right yard of
 * a building map): what the rock beyond that edge is made of, at the same heights and in the same material.
 */
export function edgeSlabs(level: { width: number; shapes: Shape[] }, side: 'left' | 'right'): Extract<Shape, { kind: 'rect' }>[] {
  return level.shapes.filter((s): s is Extract<Shape, { kind: 'rect' }> =>
    s.kind === 'rect' && (side === 'left' ? s.x <= 0 && s.x + s.w > 0 : s.x + s.w >= level.width && s.x < level.width),
  );
}

/** A catapult emplacement: where it stands (ground y) and which way it faces. */
export interface CatapultDef {
  x: number;
  y: number;
  dir: 1 | -1;
}

export interface LevelDef {
  id: MapId;
  name: string;
  width: number;
  height: number;
  /** Deadly water line, or null when the bottom is a pit. */
  waterY: number | null;
  shapes: Shape[];
  boats: BoatDef[];
  /** Spawn spots by side: [0] left team, [1] right team, [2] extras (co-op waves, third/fourth teams). */
  spawns: [number, number][][];
  /** Where bought floatie moles sit, by side. */
  floats: [number, number][][];
  /** Catapults, one per side on the canyon maps: free boulders for whoever stands beside one. */
  catapults?: CatapultDef[];
  /** What bought floatie moles ride on a water map (rings by default). */
  floatStyle?: FloatStyle;
  /** Multipliers: gravity for moles and projectiles, walking budget, jump strength. */
  gravity: number;
  stamina: number;
  jump: number;
  style: LevelStyle;
  blurb: string;
}

const W = 2400;
const H = 1200;
/**
 * The water's surface on the water maps. Raft decks sit 40 px above it, so the lower log is in
 * the water; a mole is out once it is DROWN_DEPTH under it (the old drowning line, 1000).
 */
const WATER = 940;
export const DROWN_DEPTH = 60;

/** A pier: a plank deck on posts that stand in the water below it. */
function pier(x: number, y: number, w: number, depth = 260): Shape[] {
  const out: Shape[] = [{ kind: 'rect', x, y, w, h: 16, m: 'wood' }];
  for (let px = x + 20; px < x + w - 20; px += 180) out.push({ kind: 'rect', x: px, y: y + 16, w: 12, h: depth, m: 'wood' });
  out.push({ kind: 'rect', x: x + w - 32, y: y + 16, w: 12, h: depth, m: 'wood' });
  return out;
}

/** The mirror image of a half-map's shapes across the middle of a level W wide. */
function flip(shapes: Shape[], width: number): Shape[] {
  return shapes.map((s): Shape => {
    switch (s.kind) {
      case 'rect':
        return { ...s, x: width - s.x - s.w };
      case 'circle':
      case 'hole':
        return { ...s, x: width - s.x };
      case 'poly':
        return { ...s, pts: s.pts.map(([x, y]) => [width - x, y] as [number, number]).reverse() };
      default:
        return s;
    }
  });
}

function flipBoats(boats: BoatDef[], width: number): BoatDef[] {
  return boats.map((b) => ({
    ...b,
    id: b.id.endsWith('-l') ? b.id.slice(0, -2) + '-r' : b.id + '-r',
    x: width - b.x,
    stops: b.stops === 'left' ? 'right' : b.stops === 'right' ? 'left' : b.stops,
    upper: b.upper ? { ...b.upper, at: -b.upper.at, ramps: b.upper.ramps === 'left' ? 'right' : b.upper.ramps === 'right' ? 'left' : b.upper.ramps } : undefined,
  }));
}

/** [x, y] spots mirrored across the middle of a level W wide. */
function flipSpots(spots: [number, number][], width: number): [number, number][] {
  return spots.map(([x, y]) => [width - x, y] as [number, number]);
}

const SUNNY: LevelStyle = {
  sky: ['#38a8ea', '#d9f3ff'], far: '#8ccfe4', near: '#52ad6e', water: 'rgba(36, 150, 226, 0.82)', deep: '#0d4f88', pit: '#0d3d5c', horizon: 'islands', art: 'water',
};
const SUNSET: LevelStyle = {
  sky: ['#ff9a62', '#ffe3b0'], far: '#c98a7c', near: '#8e5a66', water: 'rgba(40, 110, 180, 0.82)', deep: '#173a62', pit: '#173a62', horizon: 'islands', art: 'sunset',
};
const OVERCAST: LevelStyle = {
  sky: ['#5f6f86', '#c9d3dc'], far: '#8497ad', near: '#5d6f84', water: 'rgba(50, 100, 140, 0.88)', deep: '#18283a', pit: '#18283a', horizon: 'storm', art: 'storm',
};

const TREE_W = 2600;
const treeLeft: Shape[] = pier(790, 880, 200);
const treeBoatsLeft: BoatDef[] = [{ id: 'left-l', x: 420, y: 900, panelW: 90, panels: 6, stops: 'both' }];
const PORT_W = 2800;
const portLeft: Shape[] = [];
const portBoatsLeft: BoatDef[] = [
  { id: 'left-l', x: 440, y: 900, panelW: 90, panels: 6, stops: 'both' },
  { id: 'fishing-l', x: 860, y: 900, panelW: 260, panels: 1, stops: 'both', kind: 'fishing' },
];
const MARINA_W = 3000;
const marinaBoatsLeft: BoatDef[] = [
  { id: 'left-l', x: 420, y: 900, panelW: 90, panels: 5, stops: 'both' },
  { id: 'yacht-l', x: 830, y: 900, panelW: 340, panels: 1, stops: 'both', kind: 'yacht', upper: { at: -10, bays: 5, bayW: 56 } },
  { id: 'fishing-l', x: 1185, y: 900, panelW: 260, panels: 1, stops: 'both', kind: 'fishing' },
];
const BEACH_W = 2600;
const beachLeft: Shape[] = [
  { kind: 'poly', pts: [[0, 820], [540, 820], [600, 860], [640, 940], [660, 1200], [0, 1200]], m: 'rock' },
  // The lifeguard tower: a deck on legs with a roof on posts.
  { kind: 'rect', x: 60, y: 770, w: 170, h: 14, m: 'wood' },
  { kind: 'rect', x: 70, y: 784, w: 12, h: 36, m: 'wood' },
  { kind: 'rect', x: 208, y: 784, w: 12, h: 36, m: 'wood' },
  { kind: 'rect', x: 68, y: 704, w: 8, h: 66, m: 'wood' },
  { kind: 'rect', x: 214, y: 704, w: 8, h: 66, m: 'wood' },
  { kind: 'rect', x: 50, y: 692, w: 190, h: 12, m: 'wood' },
  ...pier(560, 820, 740, 380),
];
const WATERPARK_W = 2600;
const parkLeft: Shape[] = [
  { kind: 'poly', pts: [[0, 880], [420, 880], [520, 940], [540, 1200], [0, 1200]], m: 'grass' },
  // The way up, the platform and the long slide down to the pool.
  { kind: 'poly', pts: [[300, 880], [520, 720], [520, 738], [300, 898]], m: 'metal' },
  { kind: 'rect', x: 520, y: 716, w: 160, h: 18, m: 'metal' },
  { kind: 'poly', pts: [[680, 716], [1100, 900], [1100, 918], [680, 734]], m: 'metal' },
  { kind: 'rect', x: 560, y: 734, w: 12, h: 360, m: 'wood' },
  { kind: 'rect', x: 640, y: 734, w: 12, h: 360, m: 'wood' },
  { kind: 'rect', x: 890, y: 817, w: 12, h: 280, m: 'wood' },
  { kind: 'rect', x: 1040, y: 874, w: 12, h: 230, m: 'wood' },
];

/**
 * A flight of stairs a mole can walk: `n` risers of `rise` px over `run` px treads, climbing
 * toward `dir` from the floor at (x, y). Each step is a column that reaches `depth` px below the
 * floor, so a flight laid on a slope sinks into it and nothing hangs in the air. Risers under
 * STEP_UP keep it a walk rather than a chain of hops.
 */
function stairs(x: number, y: number, n: number, dir: 1 | -1, m: Material, run = 20, rise = 8, depth = 0): Shape[] {
  const out: Shape[] = [];
  for (let i = 0; i < n; i++) {
    const sx = dir === 1 ? x + i * run : x - (i + 1) * run;
    out.push({ kind: 'rect', x: sx, y: y - (i + 1) * rise, w: run, h: (i + 1) * rise + depth, m });
  }
  return out;
}

const PIRATE_W = 2600;
/**
 * The pirate ship lies beached on a reef, bow to the left, listing so the deck climbs toward the
 * stern. A plank runs down from each end of the deck to a hump of the reef, and the crews moor
 * their rafts against the reef below.
 */
const pirateShapes: Shape[] = [
  // The reef: a hump for each plank to land on and a saddle the keel rests in.
  { kind: 'poly', m: 'rock', pts: [[700, 940], [750, 892], [820, 866], [890, 880], [960, 870], [1020, 892], [1300, 912], [1580, 892], [1640, 866], [1710, 870], [1780, 866], [1850, 892], [1900, 940], [1900, 1200], [700, 1200]] },
  // The hull: the deck runs from the bow at (930, 796) up to the stern at (1670, 756).
  { kind: 'poly', m: 'wood', pts: [[930, 796], [1670, 756], [1660, 850], [1580, 900], [1400, 922], [1150, 922], [1000, 900], [950, 850]] },
  // Two crates on the foredeck and the cabin aft: cover on the deck, and a hop to get past.
  { kind: 'rect', x: 1040, y: 740, w: 56, h: 56, m: 'wood' },
  { kind: 'rect', x: 1096, y: 740, w: 56, h: 56, m: 'wood' },
  { kind: 'rect', x: 1500, y: 704, w: 140, h: 66, m: 'wood' },
  // The mast with two yards and the crow's nest: perches for whoever brings a grapple. The yards
  // stay well over 160 px above the deck, the band a spawn scans for ground, so nobody spawns on one.
  { kind: 'rect', x: 1288, y: 420, w: 24, h: 370, m: 'wood' },
  { kind: 'rect', x: 1150, y: 590, w: 300, h: 10, m: 'wood' },
  { kind: 'rect', x: 1210, y: 510, w: 180, h: 10, m: 'wood' },
  { kind: 'rect', x: 1246, y: 460, w: 108, h: 14, m: 'wood' },
  // A gap at the foot of the mast, like the door in Tree Island's trunk, so the deck stays a walk.
  { kind: 'hole', x: 1300, y: 732, r: 44 },
  // The gangways: a plank from each end of the deck down to the reef.
  { kind: 'poly', m: 'wood', pts: [[935, 796], [935, 810], [825, 882], [825, 868]] },
  { kind: 'poly', m: 'wood', pts: [[1665, 756], [1665, 770], [1775, 882], [1775, 868]] },
];
const pirateBoatsLeft: BoatDef[] = [{ id: 'left-l', x: 420, y: 900, panelW: 90, panels: 6, stops: 'both' }];

const LINER_W = 3000;
/**
 * The liner: 900 px of dark hull with a raked bow to the left and a square stern to the right.
 * The main deck stands a hop above the moored boats' decks; the pale promenade deck above it is
 * reached by a flight of stairs at each end, and the bridge and the two funnels on top are cover
 * to duck behind, a hop high.
 */
const linerShapes: Shape[] = [
  { kind: 'poly', m: 'metal', pts: [[1050, 856], [1950, 856], [1970, 900], [1960, 980], [1150, 980]] },
  ...stairs(1110, 856, 10, 1, 'stone'),
  ...stairs(1890, 856, 10, -1, 'stone'),
  { kind: 'rect', x: 1310, y: 776, w: 380, h: 80, m: 'stone' },
  { kind: 'rect', x: 1440, y: 732, w: 120, h: 44, m: 'stone' },
  { kind: 'rect', x: 1376, y: 728, w: 48, h: 48, m: 'wood' },
  { kind: 'rect', x: 1576, y: 728, w: 48, h: 48, m: 'wood' },
];
const linerBoats: BoatDef[] = [
  { id: 'left', x: 420, y: 900, panelW: 90, panels: 6, stops: 'both' },
  { id: 'yacht', x: 890, y: 900, panelW: 340, panels: 1, stops: 'both', kind: 'yacht', upper: { at: -10, bays: 5, bayW: 56 } },
  { id: 'fishing', x: 2110, y: 900, panelW: 260, panels: 1, stops: 'both', kind: 'fishing' },
  { id: 'right', x: 2540, y: 900, panelW: 90, panels: 6, stops: 'both' },
];

const DIG_W = 2800;
/**
 * A beach on each side with a channel of open water behind it for the floaties (the boardwalk
 * is too low for them to sit under), the two joined by that boardwalk, which the police boat
 * moored in its gap meets deck to deck.
 */
const digLeft: Shape[] = [
  // The grassy bank at the back, then the flat sand running down under the boardwalk's root.
  { kind: 'poly', m: 'grass', pts: [[150, 940], [210, 860], [290, 816], [360, 840], [360, 1200], [150, 1200]] },
  { kind: 'poly', m: 'rock', pts: [[300, 840], [740, 840], [790, 866], [820, 896], [850, 940], [870, 1200], [300, 1200]] },
  // The spoil heap the dig has thrown up.
  { kind: 'circle', x: 582, y: 858, r: 36, m: 'rock' },
  ...pier(800, 896, 480, 260),
];
/** The bulldozer on the left beach, blade down against the spoil heap: treads, body, cab, stack, arm, blade. */
const dozer: Shape[] = [
  { kind: 'rect', x: 360, y: 804, w: 180, h: 36, m: 'metal' },
  { kind: 'rect', x: 380, y: 766, w: 130, h: 40, m: 'metal' },
  { kind: 'rect', x: 436, y: 720, w: 74, h: 48, m: 'metal' },
  { kind: 'rect', x: 394, y: 736, w: 10, h: 32, m: 'metal' },
  { kind: 'rect', x: 510, y: 782, w: 44, h: 10, m: 'metal' },
  { kind: 'rect', x: 546, y: 786, w: 18, h: 56, m: 'metal' },
];
/** The dump truck on the right beach, bed tipped toward the heap behind it: wheels, chassis, cab, bed. */
const truck: Shape[] = [
  { kind: 'circle', x: 2314, y: 822, r: 18, m: 'metal' },
  { kind: 'circle', x: 2464, y: 822, r: 18, m: 'metal' },
  { kind: 'rect', x: 2284, y: 794, w: 210, h: 12, m: 'metal' },
  { kind: 'rect', x: 2434, y: 740, w: 66, h: 56, m: 'metal' },
  { kind: 'poly', m: 'metal', pts: [[2280, 796], [2424, 796], [2420, 736], [2280, 766]] },
];

const BIGTOP_W = 3000;
/** The sandy hill under the big top: a plateau with a slope down to the water on each side. */
const bigtopHill: Shape = { kind: 'poly', m: 'rock', pts: [[1020, 940], [1060, 900], [1260, 800], [1740, 800], [1940, 900], [1980, 940], [1980, 1200], [1020, 1200]] };
/**
 * One side of the big top, mirrored for the other: the bleachers up the slope, the tent wall and
 * the doorway cut through its foot. The walls and roof are separate pieces so the ring inside
 * is open: go in one door and out the other under cover.
 */
const bigtopSide: Shape[] = [
  ...stairs(1060, 880, 10, 1, 'wood', 20, 8, 30),
  { kind: 'poly', m: 'regolith', pts: [[1300, 800], [1338, 590], [1372, 590], [1334, 800]] },
  { kind: 'hole', x: 1318, y: 758, r: 48 },
];
/** The roof is thick enough that the wall tops sit inside it, eaves hanging out past the walls, and the flag pole on the peak. */
const bigtopRoof: Shape[] = [
  { kind: 'poly', m: 'regolith', pts: [[1306, 612], [1500, 420], [1694, 612], [1694, 672], [1500, 480], [1306, 672]] },
  { kind: 'rect', x: 1496, y: 340, w: 8, h: 94, m: 'wood' },
];
const bigtopBoats: BoatDef[] = [
  { id: 'left', x: 410, y: 900, panelW: 90, panels: 6, stops: 'both' },
  // The trawler under the left pier's end: the stepping stone from the raft to the pier.
  { id: 'trawler', x: 830, y: 900, panelW: 260, panels: 1, stops: 'left', kind: 'fishing' },
  { id: 'right', x: 2590, y: 900, panelW: 90, panels: 6, stops: 'both' },
];

export const LEVELS: Record<AuthoredMapId, LevelDef> = {
  // ---- Raft Wars scenes: a set piece in the water, a way across, two crews ----

  treeisle: {
    id: 'treeisle', name: 'Tree Island', width: TREE_W, height: H, waterY: WATER, gravity: 1, stamina: 1, jump: 1, floatStyle: 'ring',
    blurb: 'A grassy island with a big tree, a pier on either side and a rock to hide behind. Climb the branches for the view.',
    style: SUNNY,
    shapes: [
      ...treeLeft,
      ...flip(treeLeft, TREE_W),
      { kind: 'poly', pts: [[990, 940], [1060, 880], [1200, 852], [1300, 846], [1400, 852], [1540, 880], [1610, 940], [1610, 1200], [990, 1200]], m: 'grass' },
      { kind: 'circle', x: 1130, y: 925, r: 52, m: 'rock' },
      { kind: 'rect', x: 1285, y: 630, w: 30, h: 222, m: 'wood' },
      { kind: 'circle', x: 1300, y: 560, r: 120, m: 'grass' },
      { kind: 'circle', x: 1190, y: 610, r: 85, m: 'grass' },
      { kind: 'circle', x: 1410, y: 610, r: 85, m: 'grass' },
      { kind: 'rect', x: 1130, y: 790, w: 150, h: 16, m: 'wood' },
      { kind: 'rect', x: 1320, y: 730, w: 150, h: 16, m: 'wood' },
      // A door at the foot of the trunk: the way through the tree.
      { kind: 'hole', x: 1300, y: 808, r: 48 },
    ],
    boats: [...treeBoatsLeft, ...flipBoats(treeBoatsLeft, TREE_W)],
    spawns: [
      [[560, 900], [480, 900], [400, 900], [320, 900]],
      flipSpots([[560, 900], [480, 900], [400, 900], [320, 900]], TREE_W),
      [[1300, 846], [1180, 858], [1420, 858], [1230, 846]],
    ],
    floats: [
      [[740, 926], [80, 926]],
      [[1860, 926], [2520, 926]],
      [[740, 926]],
    ],
  },

  port: {
    id: 'port', name: 'Container Port', width: PORT_W, height: H, waterY: WATER, gravity: 1, stamina: 1.15, jump: 1, floatStyle: 'ring',
    blurb: 'A stone quay stacked with containers under a gantry crane, a trawler on one side and a police boat on the other.',
    style: OVERCAST,
    shapes: [
      { kind: 'rect', x: 1010, y: 880, w: 780, h: 320, m: 'stone' },
      // Containers stacked in a stepped pyramid, one box per step: climb it or shell over it.
      { kind: 'rect', x: 1100, y: 820, w: 120, h: 60, m: 'metal' },
      { kind: 'rect', x: 1220, y: 820, w: 120, h: 60, m: 'metal' },
      { kind: 'rect', x: 1220, y: 760, w: 120, h: 60, m: 'metal' },
      { kind: 'rect', x: 1340, y: 820, w: 120, h: 60, m: 'metal' },
      { kind: 'rect', x: 1340, y: 760, w: 120, h: 60, m: 'metal' },
      { kind: 'rect', x: 1340, y: 700, w: 120, h: 60, m: 'metal' },
      { kind: 'rect', x: 1460, y: 820, w: 120, h: 60, m: 'metal' },
      { kind: 'rect', x: 1460, y: 760, w: 120, h: 60, m: 'metal' },
      { kind: 'rect', x: 1580, y: 820, w: 120, h: 60, m: 'metal' },
      // The gantry crane: two legs and a beam, high above the stacks.
      { kind: 'rect', x: 1250, y: 490, w: 18, h: 200, m: 'metal' },
      { kind: 'rect', x: 1532, y: 490, w: 18, h: 200, m: 'metal' },
      { kind: 'rect', x: 1235, y: 478, w: 330, h: 22, m: 'metal' },
    ],
    boats: [
      ...portBoatsLeft,
      { id: 'left-r', x: PORT_W - 440, y: 900, panelW: 90, panels: 6, stops: 'both' },
      { id: 'police-r', x: PORT_W - 860, y: 900, panelW: 260, panels: 1, stops: 'both', kind: 'police' },
    ],
    spawns: [
      [[620, 900], [540, 900], [460, 900], [380, 900]],
      flipSpots([[620, 900], [540, 900], [460, 900], [380, 900]], PORT_W),
      [[1060, 880], [1750, 880], [1030, 880], [1770, 880]],
    ],
    floats: [
      [[60, 926]],
      [[2740, 926]],
      [[60, 926]],
    ],
  },

  marina: {
    id: 'marina', name: 'Marina', width: MARINA_W, height: H, waterY: WATER, gravity: 1, stamina: 1.25, jump: 1, floatStyle: 'jetski',
    blurb: 'A row of moored boats: two yachts with sun decks, two trawlers and a police boat in the middle. Sink one and the path breaks.',
    style: SUNSET,
    shapes: [],
    boats: [
      ...marinaBoatsLeft,
      { id: 'police', x: 1500, y: 900, panelW: 240, panels: 1, stops: 'both', kind: 'police' },
      ...flipBoats(marinaBoatsLeft, MARINA_W),
    ],
    spawns: [
      [[880, 824], [800, 824], [560, 900], [480, 900]],
      flipSpots([[880, 824], [800, 824], [560, 900], [480, 900]], MARINA_W),
      [[1450, 900], [1550, 900], [1500, 900], [1120, 900]],
    ],
    floats: [
      [[70, 926], [140, 926]],
      [[2930, 926], [2860, 926]],
      [[70, 926]],
    ],
  },

  beach: {
    id: 'beach', name: 'Lifeguard Beach', width: BEACH_W, height: H, waterY: WATER, gravity: 1, stamina: 1.2, jump: 1, floatStyle: 'jetski',
    blurb: 'Two beaches with lifeguard towers and a long boardwalk between them. Jetskis wait in the water under it.',
    style: SUNNY,
    shapes: [...beachLeft, ...flip(beachLeft, BEACH_W)],
    boats: [],
    spawns: [
      [[500, 820], [440, 820], [380, 820], [320, 820]],
      flipSpots([[500, 820], [440, 820], [380, 820], [320, 820]], BEACH_W),
      [[1300, 820], [1200, 820], [1400, 820], [1100, 820]],
    ],
    floats: [
      [[760, 926], [960, 926]],
      [[1840, 926], [1640, 926]],
      [[1300, 926]],
    ],
  },

  waterpark: {
    id: 'waterpark', name: 'Waterpark', width: WATERPARK_W, height: H, waterY: WATER, gravity: 1, stamina: 1.1, jump: 1, floatStyle: 'ring',
    blurb: 'Climb the ramp to the platform, ride the long slide down into the pool and take the pool back. Slides and pillars crumble.',
    style: SUNNY,
    shapes: [...parkLeft, ...flip(parkLeft, WATERPARK_W)],
    boats: [{ id: 'pool', x: 1300, y: 900, panelW: 100, panels: 4, stops: 'none' }],
    spawns: [
      [[640, 716], [560, 716], [280, 880], [200, 880]],
      flipSpots([[640, 716], [560, 716], [280, 880], [200, 880]], WATERPARK_W),
      [[1300, 900], [1240, 900], [1360, 900], [1180, 900]],
    ],
    floats: [
      [[720, 926], [820, 926]],
      [[1780, 926], [1880, 926]],
      [[1300, 960]],
    ],
  },

  pirate: {
    id: 'pirate', name: 'Pirate Cove', width: PIRATE_W, height: H, waterY: WATER, gravity: 1, stamina: 1.1, jump: 1, floatStyle: 'ring',
    blurb: 'A pirate ship beached on the reef, listing, with a lookout high on the mast. Planks run down to the rocks where the crews moor.',
    style: SUNSET,
    shapes: pirateShapes,
    boats: [...pirateBoatsLeft, ...flipBoats(pirateBoatsLeft, PIRATE_W)],
    spawns: [
      [[600, 900], [520, 900], [440, 900], [360, 900]],
      flipSpots([[600, 900], [520, 900], [440, 900], [360, 900]], PIRATE_W),
      [[1200, 781], [1420, 770], [1000, 792], [1570, 704]],
    ],
    floats: [
      [[60, 926], [110, 926]],
      [[2540, 926], [2490, 926]],
      [[60, 926]],
    ],
  },

  liner: {
    id: 'liner', name: 'Ocean Liner', width: LINER_W, height: H, waterY: WATER, gravity: 1, stamina: 1.2, jump: 1, floatStyle: 'jetski',
    blurb: 'An ocean liner: up the stairs to the promenade deck, over the bridge and the funnels, down the far side. A yacht is moored at the bow, a trawler at the stern.',
    style: OVERCAST,
    shapes: linerShapes,
    boats: linerBoats,
    spawns: [
      [[930, 824], [850, 824], [600, 900], [520, 900]],
      [[2070, 900], [2150, 900], [2360, 900], [2440, 900]],
      [[1500, 732], [1080, 856], [1920, 856], [1340, 776]],
    ],
    floats: [
      [[50, 926], [100, 926]],
      [[2950, 926], [2900, 926]],
      [[50, 926]],
    ],
  },

  digsite: {
    id: 'digsite', name: 'Beach Dig', width: DIG_W, height: H, waterY: WATER, gravity: 1, stamina: 1.2, jump: 1, floatStyle: 'jetski',
    blurb: 'Two beaches mid-dig, a bulldozer on one and a dump truck on the other, joined by a low boardwalk with a police boat moored in the gap. Sink it and the walk is over.',
    style: SUNNY,
    shapes: [...digLeft, ...flip(digLeft, DIG_W), ...dozer, ...truck],
    boats: [{ id: 'police', x: 1400, y: 900, panelW: 240, panels: 1, stops: 'none', kind: 'police' }],
    spawns: [
      [[735, 840], [695, 840], [655, 840], [625, 840]],
      flipSpots([[735, 840], [695, 840], [655, 840], [625, 840]], DIG_W),
      [[1400, 900], [1200, 896], [1600, 896], [1340, 900]],
    ],
    floats: [
      [[50, 926], [100, 926]],
      [[2750, 926], [2700, 926]],
      [[50, 926]],
    ],
  },

  bigtop: {
    id: 'bigtop', name: 'Circus Beach', width: BIGTOP_W, height: H, waterY: WATER, gravity: 1, stamina: 1.25, jump: 1, floatStyle: 'ring',
    blurb: 'A big top on the hill between two piers. Climb the bleachers and go in one door and out the other, or take the roof.',
    style: SUNSET,
    shapes: [
      bigtopHill,
      ...bigtopSide,
      ...flip(bigtopSide, BIGTOP_W),
      ...bigtopRoof,
      // The left pier rests its outer end on the trawler; the right one reaches out toward the raft.
      { kind: 'rect', x: 860, y: 880, w: 200, h: 16, m: 'wood' },
      { kind: 'rect', x: 1040, y: 896, w: 12, h: 260, m: 'wood' },
      ...pier(1940, 880, 300),
    ],
    boats: bigtopBoats,
    spawns: [
      [[1020, 880], [940, 880], [590, 900], [510, 900]],
      [[1980, 880], [2060, 880], [2410, 900], [2490, 900]],
      [[1500, 800], [1420, 800], [1580, 800], [1280, 800]],
    ],
    floats: [
      [[50, 926], [100, 926]],
      [[2950, 926], [2900, 926]],
      [[50, 926]],
    ],
  },
};

/** Authored levels only; maps.ts resolves war, generated and random ids too. */
export function getLevel(id: MapId): LevelDef {
  return LEVELS[id as AuthoredMapId] ?? LEVELS.treeisle;
}

/** Rasterize the authored shapes into a collision mask (boats are not part of it). */
export function buildTerrain(level: LevelDef): Terrain {
  const t = new Terrain(level.width, level.height);
  for (const s of level.shapes) {
    switch (s.kind) {
      case 'rect':
        t.fillRect(s.x, s.y, s.w, s.h);
        break;
      case 'circle':
        t.fillCircle(s.x, s.y, s.r);
        break;
      case 'poly':
        t.fillPolygon(s.pts);
        break;
      case 'mask':
        eachMaskRun(s, (x, y, w, h) => t.fillRect(x, y, w, h));
        break;
      case 'hole':
        t.carve(s.x, s.y, s.r);
        break;
    }
  }
  return t;
}

const maskCache = new Map<string, Uint8Array>();

/** The cells of a mask shape: 1 where there is ground, row-major, `cols * rows` long. */
export function decodeMaskShape(s: Extract<Shape, { kind: 'mask' }>): Uint8Array {
  const hit = maskCache.get(s.rle);
  if (hit) return hit;
  const cols = Math.ceil(s.w / s.cell);
  const rows = Math.ceil(s.h / s.cell);
  const cells = new Uint8Array(cols * rows);
  let i = 0;
  let v = 0;
  for (const part of s.rle.split(',')) {
    const n = parseInt(part, 36);
    if (v === 1) cells.fill(1, i, Math.min(cells.length, i + n));
    i += n;
    v ^= 1;
  }
  maskCache.set(s.rle, cells);
  return cells;
}

/** Calls back once per horizontal run of ground in a mask shape, in world px. */
export function eachMaskRun(s: Extract<Shape, { kind: 'mask' }>, fn: (x: number, y: number, w: number, h: number) => void): void {
  const cols = Math.ceil(s.w / s.cell);
  const rows = Math.ceil(s.h / s.cell);
  const cells = decodeMaskShape(s);
  for (let r = 0; r < rows; r++) {
    let c = 0;
    while (c < cols) {
      if (!cells[r * cols + c]) {
        c++;
        continue;
      }
      const c0 = c;
      while (c < cols && cells[r * cols + c]) c++;
      fn(s.x + c0 * s.cell, s.y + r * s.cell, (c - c0) * s.cell, s.cell);
    }
  }
}

/** Y a unit should stand at when dropped onto column x near y (terrain only). */
export function groundAt(t: Terrain, x: number, y: number): number {
  return t.surfaceY(x, Math.max(0, y - 160)) - 1;
}

/** First float spot on a side that no living mole already occupies. */
export function freeFloat(level: LevelDef, side: number, occupied: { x: number; y: number }[]): [number, number] | null {
  const lists = [level.floats[side] ?? [], ...level.floats];
  for (const list of lists) {
    for (const spot of list) {
      if (!occupied.some((u) => Math.abs(u.x - spot[0]) < 30 && Math.abs(u.y - spot[1]) < 60)) return spot;
    }
  }
  return null;
}

/** A spawn spot on a side with nobody standing on it, or a nudge beside an occupied one. */
export function freeSpawn(level: LevelDef, side: number, occupied: { x: number; y: number }[]): [number, number] {
  const list = level.spawns[side] ?? level.spawns[0]!;
  for (const spot of list) {
    if (!occupied.some((u) => Math.abs(u.x - spot[0]) < 40 && Math.abs(u.y - spot[1]) < 80)) return spot;
  }
  const base = list[list.length - 1] ?? [level.width / 2, 0];
  return [base[0] + (side === 0 ? -44 : 44) * (1 + (occupied.length % 3)), base[1]];
}

/** Flat cartoon fills: the body colour, and the lighter cap the decor paints along top surfaces. */
const MATERIAL_FILL: Record<Material, { body: string; cap: string }> = {
  grass: { body: '#bd7c47', cap: '#7fd957' },
  wood: { body: '#c98c4e', cap: '#eab46f' },
  stone: { body: '#9da3ad', cap: '#d3d8df' },
  metal: { body: '#7e8a97', cap: '#b7c3cf' },
  rock: { body: '#a39079', cap: '#d2c2aa' },
  regolith: { body: '#aeaeb8', cap: '#e0e0e8' },
};

/** How deep the cap band reaches below a top surface, in world px. */
export const CAP_DEPTH = 12;

function shapePath(ctx: CanvasRenderingContext2D, s: Exclude<Shape, { kind: 'hole' }>): void {
  ctx.beginPath();
  if (s.kind === 'rect') ctx.rect(s.x, s.y, s.w, s.h);
  else if (s.kind === 'circle') ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
  else if (s.kind === 'mask') eachMaskRun(s, (x, y, w, h) => ctx.rect(x, y, w, h));
  else {
    ctx.moveTo(s.pts[0]![0], s.pts[0]![1]);
    for (const [x, y] of s.pts.slice(1)) ctx.lineTo(x, y);
    ctx.closePath();
  }
}

function punch(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.save();
  ctx.globalCompositeOperation = 'destination-out';
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/**
 * Paint the level's static ground: flat fills with a little texture, plus a second picture of
 * the same shapes in cap colours. Outlines and the cap band itself are added at draw time by
 * GroundDecor, so craters get them too. Browser only.
 */
export function paintLevel(level: LevelDef): GroundArt {
  const fill = document.createElement('canvas');
  fill.width = level.width;
  fill.height = level.height;
  const cap = document.createElement('canvas');
  cap.width = level.width;
  cap.height = level.height;
  const fc = fill.getContext('2d')!;
  const cc = cap.getContext('2d')!;
  for (const s of level.shapes) {
    if (s.kind === 'hole') {
      punch(fc, s.x, s.y, s.r);
      punch(cc, s.x, s.y, s.r + CAP_DEPTH * 0.8);
      continue;
    }
    const m = MATERIAL_FILL[s.m];
    shapePath(fc, s);
    fc.fillStyle = m.body;
    fc.fill();
    fc.save();
    fc.clip();
    paintTexture(fc, s);
    fc.restore();
    shapePath(cc, s);
    cc.fillStyle = m.cap;
    cc.fill();
  }
  return { fill, cap, capDepth: CAP_DEPTH };
}

/** A few flat marks per material so big slabs are not plain colour. */
function paintTexture(ctx: CanvasRenderingContext2D, s: Exclude<Shape, { kind: 'hole' }>): void {
  const b = bounds(s);
  const area = (b.x1 - b.x0) * (b.y1 - b.y0);
  switch (s.m) {
    case 'wood':
      ctx.strokeStyle = 'rgba(70, 35, 10, 0.28)';
      ctx.lineWidth = 2;
      for (let x = b.x0 + 34; x < b.x1; x += 34) {
        ctx.beginPath();
        ctx.moveTo(x, b.y0);
        ctx.lineTo(x, b.y1);
        ctx.stroke();
      }
      ctx.fillStyle = 'rgba(70, 35, 10, 0.22)';
      for (let x = b.x0 + 17; x < b.x1; x += 34) {
        for (let y = b.y0 + 12; y < b.y1; y += 60) ctx.fillRect(x - 1.5, y, 3, 3);
      }
      break;
    case 'stone':
      ctx.strokeStyle = 'rgba(0,0,0,0.14)';
      ctx.lineWidth = 2;
      for (let y = b.y0 + 40; y < b.y1; y += 40) {
        ctx.beginPath();
        ctx.moveTo(b.x0, y);
        ctx.lineTo(b.x1, y);
        ctx.stroke();
        const off = ((y / 40) | 0) % 2 ? 40 : 0;
        for (let x = b.x0 + off; x < b.x1; x += 80) {
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(x, y + 40);
          ctx.stroke();
        }
      }
      break;
    case 'metal':
      ctx.fillStyle = 'rgba(255,255,255,0.16)';
      for (let y = b.y0 + 12; y < b.y1; y += 46) {
        for (let x = b.x0 + 12; x < b.x1; x += 46) {
          ctx.beginPath();
          ctx.arc(x, y, 3, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      break;
    case 'rock':
    case 'grass':
    case 'regolith': {
      // Scattered darker stones, deterministic per shape.
      const n = Math.min(400, Math.round(area / 6000));
      ctx.fillStyle = s.m === 'regolith' ? 'rgba(0,0,0,0.14)' : 'rgba(60, 30, 10, 0.16)';
      for (let i = 0; i < n; i++) {
        const x = b.x0 + ((i * 7919 + 13) % Math.max(1, b.x1 - b.x0));
        const y = b.y0 + CAP_DEPTH + 6 + ((i * 104729 + 7) % Math.max(1, b.y1 - b.y0 - CAP_DEPTH - 6));
        const r = 2.5 + (i % 3) * 1.5;
        ctx.beginPath();
        ctx.ellipse(x, y, r * 1.4, r, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }
  }
}

function bounds(s: Exclude<Shape, { kind: 'hole' }>): { x0: number; y0: number; x1: number; y1: number } {
  if (s.kind === 'rect' || s.kind === 'mask') return { x0: s.x, y0: s.y, x1: s.x + s.w, y1: s.y + s.h };
  if (s.kind === 'circle') return { x0: s.x - s.r, y0: s.y - s.r, x1: s.x + s.r, y1: s.y + s.r };
  const xs = s.pts.map((p) => p[0]);
  const ys = s.pts.map((p) => p[1]);
  return { x0: Math.min(...xs), y0: Math.min(...ys), x1: Math.max(...xs), y1: Math.max(...ys) };
}
